"use client"

import { useEffect, useRef } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Link from "next/link"
import { useTranslation } from "react-i18next"
import { ROUTES } from "@src/constants"
import metadataConfig from "@src/constants/metadata.json"
import { useFloatingActions } from "@src/components/FloatingActions/context"
import "./style.css"

type ContactAction = {
  id: string
  label: string
  href: string
  external?: boolean
  icon: string
}

const FAButton = () => {
  const { t, i18n } = useTranslation()
  const { openPanel, togglePanel, setOpenPanel } = useFloatingActions()
  const rootRef = useRef<HTMLDivElement>(null)
  const open = openPanel === "contact"

  const actions: ContactAction[] = [
    {
      id: "zalo",
      label: "Zalo",
      href: `https://zalo.me/${metadataConfig.contact.hotline}`,
      external: true,
      icon: "zalo"
    },
    {
      id: "messenger",
      label: "Messenger",
      href: "http://m.me/theciusaigon",
      external: true,
      icon: "messenger"
    },
    {
      id: "phone",
      label: i18n.language === "en" ? "Call" : "Gọi điện",
      href: `tel:${metadataConfig.contact.hotline_cskh}`,
      icon: "phone"
    },
    {
      id: "branch",
      label: t("branch"),
      href: ROUTES.BRANCH,
      icon: "location"
    }
  ]

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpenPanel(null)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenPanel(null)
    }

    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open, setOpenPanel])

  return (
    <Box
      ref={rootRef}
      className="fab-wrapper"
      sx={{ position: "relative", zIndex: 1 }}
    >
      <button
        type="button"
        className={`fab${open ? " fab--open" : " fab--pulse"}`}
        aria-label={t("contact")}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => togglePanel("contact")}
      >
        <span className="fab-contact-icon" aria-hidden>
          <svg viewBox="0 0 24 24" className="fab-contact-svg">
            <g transform="translate(0 -0.6)">
              <path
                className="fab-contact-bubble"
                d="M5 5.4C5 4.18 5.98 3.2 7.2 3.2h9.6c1.22 0 2.2.98 2.2 2.2v7.2c0 1.22-.98 2.2-2.2 2.2h-2.85l-2.9 2.6c-.5.45-1.3.09-1.3-.6v-2H7.2c-1.22 0-2.2-.98-2.2-2.2V5.4Z"
              />
              <circle className="fab-dot fab-dot-a" cx="8.8" cy="9" r="1.15" />
              <circle className="fab-dot fab-dot-b" cx="12" cy="9" r="1.15" />
              <circle className="fab-dot fab-dot-c" cx="15.2" cy="9" r="1.15" />
            </g>
          </svg>
        </span>
        <span className="fab-dots fab-dots-1" />
        <span className="fab-dots fab-dots-2" />
        <span className="fab-dots fab-dots-3" />
      </button>

      <Box
        role="menu"
        aria-label={t("contact")}
        aria-hidden={!open}
        sx={{
          position: "absolute",
          right: "calc(100% + 1rem)",
          bottom: 0,
          width: "14rem",
          maxHeight: "min(24rem, calc(100vh - 2.4rem))",
          borderRadius: "1rem",
          bgcolor: "#fff",
          border: "1px solid #E8E8E8",
          boxShadow: "0 1.2rem 3.2rem rgba(0,0,0,0.16)",
          opacity: open ? 1 : 0,
          visibility: open ? "visible" : "hidden",
          transform: open ? "translateX(0)" : "translateX(0.5rem)",
          transition:
            "opacity 160ms ease, transform 160ms ease, visibility 160ms ease",
          pointerEvents: open ? "auto" : "none",
          zIndex: 3,
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            px: "1.2rem",
            pt: "1rem",
            pb: "0.7rem",
            borderBottom: "1px solid #F0F0F0",
            display: "flex",
            alignItems: "center",
            gap: "0.8rem"
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: "0.95rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#8A8A8A",
              lineHeight: 1
            }}
          >
            {t("contact")}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label={i18n.language === "en" ? "Close" : "Đóng"}
            onClick={() => setOpenPanel(null)}
            sx={{
              all: "unset",
              boxSizing: "border-box",
              cursor: "pointer",
              width: "1.6rem",
              height: "1.6rem",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#B0B0B0",
              fontSize: "1.3rem",
              lineHeight: 1,
              transition: "background-color 120ms ease, color 120ms ease",
              "&:hover": {
                bgcolor: "#F0F0F0",
                color: "#141414"
              }
            }}
          >
            ×
          </Box>
        </Box>

        <Box sx={{ py: "0.4rem" }}>
          {actions.map(action => (
            <Box
              key={action.id}
              component={Link}
              role="menuitem"
              href={action.href}
              target={action.external ? "_blank" : undefined}
              rel={action.external ? "noopener noreferrer" : undefined}
              onClick={() => setOpenPanel(null)}
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: "0.8rem",
                px: "1.2rem",
                py: "0.75rem",
                textDecoration: "none",
                color: "#141414",
                transition: "background-color 120ms ease",
                "&:hover": {
                  bgcolor: "#F6F6F6"
                }
              }}
            >
              <Box
                className={`fab-menu-icon ${action.icon}`}
                sx={{
                  width: "2.2rem",
                  height: "2.2rem",
                  flexShrink: 0,
                  borderRadius: "999px",
                  bgcolor: "#F6F6F6",
                  backgroundSize: "60% 60%",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat"
                }}
              />
              <Typography
                component="span"
                sx={{
                  flex: 1,
                  fontSize: "1.2rem",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  lineHeight: 1,
                  color: "inherit"
                }}
              >
                {action.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default FAButton
